import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule, provideZoneChangeDetection } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MAT_SELECT_CONFIG, MatSelectConfig } from '@angular/material/select';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { CanvasRendererHostHttpInterceptor } from 'src/app/canvas-renderer/canvas-renderer-host.http-interceptor';
import { CanvasRendererComponent } from 'src/app/canvas-renderer/canvas-renderer.component';
import { ErrorPageComponent } from 'src/app/error/error-page.component';
import { setAppInjector } from './app-injector.module';
import './app-locales.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthHttpInterceptor } from './auth/auth.http-interceptor';
import { AuthModule } from './auth/auth.module';
import { ContentService } from './content/content.service';
import { DenyBusinessUserGuard } from './deny-business-user.guard';
import { EnterpriseReportingGuard } from './enterprise-reporting/enterprise-reporting.guard';
import { GainsightPX } from './gainsightPX/gainsightPX.service';
import { InsightsJobsModule } from './insights-jobs/insights-jobs.module';
import { InsightsLibraryModule } from './insights-library/insights-library.module';
import { LayoutModule } from './layout/layout.module';
import { LogoutComponent } from './logout/components/logout.component';
import { LogoutGuard } from './logout/logout.guard';
import { DataModelResolver } from './pbir/dataModel/DataModelResolver';
import { PbirModule } from './pbir/PbirModule';
import { UserDatasetLoader } from './pbir/userDatasets/UserDatasetLoader';
import { CustomFieldFunctionResolver } from './pbir/visuals/customFields/CustomFieldFunctionResolver';
import { DataQueryCache } from './pbir/visuals/data/impl/DataQuery/DataQueryCache';
import { IndexedDBDataQueryCache } from './pbir/visuals/data/impl/DataQuery/IndexedDBDataQueryCache';
import { AppAdminApiModule, AppApiModule } from './services/api.module';
import { ClientCustomizationService, NotificationService } from './services/api.services';
import { AppInitService } from './services/app-init.service';
import { AppSettingsService } from './services/app-settings.service';
import { AssetLocks } from './services/AssetLocks.service';
import { SnackService } from './services/snack.service';
import { SharedModule } from './shared/shared.module';
import { AppEffects } from './state/app/app.effects';
import * as AppReducer from './state/app/app.reducer';
import { UIModule } from './ui/ui.module';
import { WelcomeModule } from './welcome/welcome.module';
import './pbir/utils/MapExtensions';

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0
  }
};

@NgModule({
  declarations: [
    AppComponent,
    ErrorPageComponent,
    CanvasRendererComponent,
    LogoutComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,

    AppRoutingModule,
    BrowserAnimationsModule,

    AuthModule,

    StoreModule.forRoot({
      app: AppReducer.reducer
    }, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      }
    }
    ),
    EffectsModule.forRoot([
      AppEffects,
    ]),

    SharedModule,
    UIModule,

    LayoutModule,

    AppAdminApiModule,
    AppApiModule,

    InsightsLibraryModule,
    InsightsJobsModule,
    WelcomeModule,

    PbirModule
  ],
  providers: [
    provideZoneChangeDetection({
      eventCoalescing: true,
      runCoalescing: true,
      ignoreChangesOutsideZone: true,
    }),

    {
      provide: HTTP_INTERCEPTORS,
      useClass: isCanvasRendererHost ? CanvasRendererHostHttpInterceptor : AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (appInitService: AppInitService) => async () => await appInitService.init(),
      deps: [AppInitService, HTTP_INTERCEPTORS]
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: <MatSelectConfig>{
        disableOptionCentering: true,
      }
    },

    DataModelResolver,
    AppSettingsService,
    DenyBusinessUserGuard,
    SnackService,
    ContentService,
    EnterpriseReportingGuard,
    CookieService,
    ClientCustomizationService,
    NotificationService,
    LogoutGuard,
    GainsightPX,

    IndexedDBDataQueryCache,
    {
      provide: DataQueryCache,
      useExisting: IndexedDBDataQueryCache
    },

    CustomFieldFunctionResolver,
    UserDatasetLoader,
    AssetLocks
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule {
  constructor(injector: Injector) {
    setAppInjector(injector);
  }
};
